import React, { useEffect, useState } from 'react';
import { Handle, Position, useEdges, useReactFlow, useUpdateNodeInternals } from 'reactflow';
import { testIds as blockNodeTestIds } from '../FlowEditorBlockNode/testIds';
import {
  Portal,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  HStack,
  Box,
} from '@chakra-ui/react';
import {
  Blocks,
  GroupIcon,
  Image,
  Link as LinkIcon,
  List,
  ListOrdered,
  MessageSquare,
  Paperclip,
} from 'lucide-react';
import {
  createUploadDBlock,
  linkJSON,
  messageJSON,
  olJSON,
  ulJSON,
} from '../../tiptap/contentJSONs';
const menuIcon = '/assets/icons/ArrowDownToDot.svg';
import { brandColors, colors } from '@duke-hq/ui/theme/chakraTheme';
import { flowEditorState } from '../../flowEditorState';
import { useAddObjectButton } from './useAddObjectButton';
import { chatMessageDBlock } from '../flowEditorBlockNodeTypes';
import { useAddContainerButton } from '../../containers/utils/useAddContainerButton';
import AddGifButton from './AddGifButton';
import type * as CSS from 'csstype';

type BlockConnectMenuProps = {
  children?: React.ReactNode;
  isConnectable: boolean;
  nodeId: string;
  option: {
    id: string;
    title: string;
  };
  isQuizMenu?: boolean;
};

const connectedStyles: Record<string, CSS.Properties> = {
  container: {},
  menuBox: {},
  handle: {
    position: 'relative',
    right: '12px',
    opacity: 0,
  },
};

const defaultStyles: Record<string, CSS.Properties> = {
  container: {},
  menuBox: {},
  handle: {
    width: '32px',
    height: '32px',
    position: 'relative',
    top: 'auto',
    transform: 'rotate(-90deg)',
    background: `${colors.quizBg}`,
    backgroundImage: `url(${menuIcon})`,
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
    backgroundPosition: 'center',
    backgroundSize: '20px 20px',
    border: `1px solid ${brandColors.gray[300]}`,
    borderRadius: '9999px',
    zIndex: '50',
    cursor: 'pointer',
  },
};

export function BlockConnectMenu({
  children,
  isConnectable,
  nodeId,
  option,
  isQuizMenu = false,
}: BlockConnectMenuProps) {
  const [menu, setMenu] = useState(false);
  const edges = useEdges();
  const updateNodeInternals = useUpdateNodeInternals();
  const { getNode } = useReactFlow();

  const message = useAddObjectButton({
    id: nodeId,
    sourceHandle: option.id,
    tiptapContent: chatMessageDBlock(messageJSON),
  });
  const uList = useAddObjectButton({
    id: nodeId,
    sourceHandle: option.id,
    tiptapContent: chatMessageDBlock(ulJSON),
  });
  const oList = useAddObjectButton({
    id: nodeId,
    sourceHandle: option.id,
    tiptapContent: chatMessageDBlock(olJSON),
  });
  const link = useAddObjectButton({
    id: nodeId,
    sourceHandle: option.id,
    tiptapContent: chatMessageDBlock(linkJSON),
  });
  const emptyBlock = useAddObjectButton({
    id: nodeId,
    sourceHandle: option.id,
  });

  const addImage = useAddObjectButton({
    id: nodeId,
    sourceHandle: option.id,
    tiptapContent: createUploadDBlock('image/*'),
  });
  const uploadFile = useAddObjectButton({
    id: nodeId,
    sourceHandle: option.id,
    tiptapContent: createUploadDBlock(),
  });
  const emptyContainerizedBlock = useAddObjectButton({
    id: nodeId,
    sourceHandle: option.id,
    isContainerizedNode: true,
    shouldRenderOutside: false,
  });
  const emptyNonContainerizedBlock = useAddObjectButton({
    id: nodeId,
    sourceHandle: option.id,
    isContainerizedNode: true,
    shouldRenderOutside: true,
  });
  const emptyContainer = useAddContainerButton({
    id: nodeId,
    sourceHandle: option.id,
  });

  const isOptionConnected = (): boolean => {
    return flowEditorState.connectedOptionIds.has(option.id);
  };

  // TODO: `updateNodeInternals` almost certainly should not be called like this.
  // There's missing dependencies for this useEffect, and no checking inside
  // to prevent unnecessary updates.
  //
  // it's also very unclear _WHY_ it exists? Can this be moved to a callback?
  useEffect(() => {
    updateNodeInternals(nodeId);
  }, [edges]);

  return (
    <HStack position="relative" left={isOptionConnected() || isQuizMenu ? 0 : '42px'} width="100%">
      {!isQuizMenu && <Box flex="1">{children}</Box>}
      <Box>
        <Handle
          id={option.id}
          key={option.id}
          type="source"
          position={Position.Right}
          isConnectable={!isOptionConnected() ? isConnectable : false}
          aria-label="Connect option"
          data-testid={blockNodeTestIds.addObjectHandle}
          style={
            isOptionConnected()
              ? connectedStyles.handle
              : { ...defaultStyles.handle, left: isQuizMenu ? '0px' : '30px' }
          }
          onClick={(e) => {
            if (!isOptionConnected()) {
              const handle = document.querySelector(`[itemid=menu-btn-${option.id}]`);

              if (handle !== null && 'click' in handle && typeof handle.click === 'function') {
                // This is not safe, it looks like this file needs to be rebuilt
                // it's not known what type of element it is, so we can't be sure
                //  it has a click method, or what happens if we try to call it.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                handle.click();
              }
            }
            e.stopPropagation();
            return false;
          }}
        />
        {!isOptionConnected() && (
          <Flex w="50px" alignItems="center" justifyContent="flex-end">
            <Menu
              onOpen={() => setMenu(true)}
              onClose={() => setMenu(false)}
              isOpen={menu}
              closeOnBlur={true}
              closeOnSelect={true}
            >
              <MenuButton
                itemID={`menu-btn-${option.id}`}
                onClick={(ev) => {
                  ev.stopPropagation(); // prevent the click from bubbling up to the parent and focusing the node in the editor. Focusing opens the right sidebar, which we don't want.
                  return false;
                }}
                as={IconButton}
                opacity={0}
                isRound={true}
                size="0px"
                variant="outline"
                aria-label="Add object"
                style={{
                  background: 'transparent',
                  border: 'none',
                }}
              />
              <Portal>
                <MenuList
                  onMouseLeave={() => setMenu(false)}
                  maxHeight="320px"
                  overflowY="auto"
                  overflowX="hidden"
                >
                  {!getNode(nodeId)?.parentNode ? (
                    <>
                      <MenuGroup color="gray.400" title="TEXT AND LAYOUT">
                        <MenuItem onClick={emptyBlock} icon={<Blocks size="16px" />}>
                          Empty block
                        </MenuItem>
                        <MenuItem onClick={message} icon={<MessageSquare size="16px" />}>
                          Message
                        </MenuItem>
                        <MenuItem onClick={uList} icon={<List size="16px" />}>
                          Bulleted list
                        </MenuItem>
                        <MenuItem onClick={oList} icon={<ListOrdered size="16px" />}>
                          Numbered list
                        </MenuItem>
                        <MenuItem onClick={link} icon={<LinkIcon size="16px" />}>
                          Link
                        </MenuItem>
                      </MenuGroup>
                      <MenuGroup color="gray.400" title="MEDIA AND CONTENT">
                        <MenuItem onClick={addImage} icon={<Image size="16px" />}>
                          Image
                        </MenuItem>
                        <AddGifButton getTargetNodeId={emptyBlock} />
                        <MenuItem onClick={uploadFile} icon={<Paperclip size="16px" />}>
                          File
                        </MenuItem>
                      </MenuGroup>
                      {/* DUKE-1369: Hiding the menu as its not used.
                      Not removing since because this will be handled in face lift */}

                      {/* <MenuGroup color="gray.400" title="GROUPING">
                        <MenuItem icon={<GroupIcon size="16px" />} onClick={emptyContainer}>
                          Container
                        </MenuItem>
                      </MenuGroup> */}
                    </>
                  ) : (
                    <>
                      <MenuGroup color="gray.400" title="INSIDE">
                        <MenuItem onClick={emptyContainerizedBlock} icon={<Blocks size="16px" />}>
                          Empty block
                        </MenuItem>
                      </MenuGroup>
                      <MenuGroup color="gray.400" title="OUTSIDE">
                        <MenuItem
                          onClick={emptyNonContainerizedBlock}
                          icon={<Blocks size="16px" />}
                        >
                          Empty block
                        </MenuItem>
                        <MenuItem icon={<GroupIcon size="16px" />} onClick={emptyContainer}>
                          Container
                        </MenuItem>
                      </MenuGroup>
                    </>
                  )}
                </MenuList>
              </Portal>
            </Menu>
          </Flex>
        )}
      </Box>
      {isQuizMenu ? <Box mt={isOptionConnected() ? 5 : 0}>{children}</Box> : null}
    </HStack>
  );
}
